import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { adminApi, adminLogout, formatApiError, getToken } from "@/lib/admin";
import OfferEditor from "@/components/admin/OfferEditor";

const TABS = ["Enquiries", "Bookings", "Packages", "Group Tours", "Testimonials", "Offers"];

const arrFromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrFromCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const lines = (a) => (a || []).join("\n");
const csv = (a) => (a || []).join(", ");
const itinToLines = (it) => (it || []).map((d) => `${d.title} | ${d.description}`).join("\n");
const itinFromLines = (s) => arrFromLines(s).map((l, i) => {
  const [title, ...rest] = l.split("|");
  return { day: i + 1, title: (title || "").trim(), description: rest.join("|").trim() };
});
const faqsToLines = (f) => (f || []).map((x) => `${x.q} | ${x.a}`).join("\n");
const faqsFromLines = (s) => arrFromLines(s).map((l) => {
  const [q, ...rest] = l.split("|");
  return { q: (q || "").trim(), a: rest.join("|").trim() };
});
const fmtDate = (d) => { try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

const STD_NOTES = "All costing is on request and depends on travel dates, hotel category, flights, number of travellers and customised requirements.\nPassports must be valid for at least 6 months from the date of return travel (for international holidays).\nRates and availability are dynamic and are confirmed only at the time of booking.";
const STD_CANCELLATION = "Cancellations are subject to airline, hotel and supplier policies at the time of booking. A detailed cancellation schedule is shared with your personalised quote before you confirm — no surprises.";

const EMPTY_PACKAGE = {
  slug: "", title: "", destination: "", destination_slug: "", region: "International",
  nights: 4, days: 5, holiday_types: [], travel_styles: [], image: "",
  highlights: [], overview: "", itinerary: [], inclusions: [], exclusions: [],
  hotels: "", transport: "", notes: arrFromLines(STD_NOTES), cancellation: STD_CANCELLATION, faqs: [],
};

function Field({ label, children }) {
  return <div><Label className="text-xs font-bold tracking-wide uppercase text-slate-400">{label}</Label><div className="mt-1">{children}</div></div>;
}

function PackageEditor({ initial, onSaved, onCancel }) {
  const [p, setP] = useState(() => ({
    ...initial,
    nights: String(initial.nights), days: String(initial.days),
    holiday_types: csv(initial.holiday_types), travel_styles: csv(initial.travel_styles),
    highlights: lines(initial.highlights), itinerary: itinToLines(initial.itinerary),
    inclusions: lines(initial.inclusions), exclusions: lines(initial.exclusions),
    notes: lines(initial.notes), faqs: faqsToLines(initial.faqs),
  }));
  const [busy, setBusy] = useState(false);
  const isNew = !initial._saved;
  const set = (k) => (e) => setP((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const payload = {
      slug: p.slug.trim(), title: p.title.trim(), destination: p.destination.trim(),
      destination_slug: p.destination_slug.trim(), region: p.region,
      nights: parseInt(p.nights, 10) || 0, days: parseInt(p.days, 10) || 0,
      holiday_types: arrFromCsv(p.holiday_types), travel_styles: arrFromCsv(p.travel_styles),
      image: p.image.trim(), highlights: arrFromLines(p.highlights), overview: p.overview,
      itinerary: itinFromLines(p.itinerary), inclusions: arrFromLines(p.inclusions),
      exclusions: arrFromLines(p.exclusions), hotels: p.hotels, transport: p.transport,
      notes: arrFromLines(p.notes), cancellation: p.cancellation, faqs: faqsFromLines(p.faqs),
      price_from: p.price_from === "" || p.price_from == null ? null : parseInt(p.price_from, 10),
      price_basis: p.price_basis || "per person · twin sharing",
      valid_till: p.valid_till || "",
      flights_included: !!p.flights_included,
    };
    try {
      if (isNew) await adminApi().post("/admin/packages", payload);
      else await adminApi().put(`/admin/packages/${initial.slug}`, payload);
      toast.success(isNew ? "Package created" : "Package updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="package-editor">
      <h3 className="font-heading text-lg font-bold text-navy">{isNew ? "New Package" : `Edit: ${initial.title}`}</h3>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Field label="Slug (url)*"><Input value={p.slug} onChange={set("slug")} disabled={!isNew} placeholder="bali-bliss-honeymoon" data-testid="pe-slug" /></Field>
        <Field label="Title*"><Input value={p.title} onChange={set("title")} data-testid="pe-title" /></Field>
        <Field label="Destination"><Input value={p.destination} onChange={set("destination")} placeholder="Bali" /></Field>
        <Field label="Destination Slug"><Input value={p.destination_slug} onChange={set("destination_slug")} placeholder="bali" /></Field>
        <Field label="Region">
          <select value={p.region} onChange={set("region")} className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
            <option>International</option><option>India</option>
          </select>
        </Field>
        <Field label="Image URL"><Input value={p.image} onChange={set("image")} placeholder="https://images.unsplash.com/..." /></Field>
        <Field label="Nights"><Input type="number" value={p.nights} onChange={set("nights")} /></Field>
        <Field label="Days"><Input type="number" value={p.days} onChange={set("days")} /></Field>
        <Field label="Holiday Types (comma)"><Input value={p.holiday_types} onChange={set("holiday_types")} placeholder="International, Honeymoon" /></Field>
        <Field label="Travel Styles (comma)"><Input value={p.travel_styles} onChange={set("travel_styles")} placeholder="Beach, Luxury" /></Field>
        <Field label="Tentative Price ₹ / person"><Input type="number" value={p.price_from ?? ""} onChange={set("price_from")} placeholder="58500 (blank = on request)" data-testid="pe-price" /></Field>
        <Field label="Price Basis"><Input value={p.price_basis ?? ""} onChange={set("price_basis")} placeholder="per person · twin sharing" /></Field>
        <Field label="Price Valid Till"><Input value={p.valid_till ?? ""} onChange={set("valid_till")} placeholder="31 Mar 2027" /></Field>
        <Field label="Flights">
          <label className="flex items-center gap-2 h-10 text-sm text-slate-600">
            <input type="checkbox" checked={!!p.flights_included} onChange={(e) => setP((s) => ({ ...s, flights_included: e.target.checked }))} />
            Flights included in price
          </label>
        </Field>
      </div>
      <div className="mt-4 grid gap-4">
        <Field label="Overview"><Textarea rows={3} value={p.overview} onChange={set("overview")} /></Field>
        <Field label="Highlights (one per line)"><Textarea rows={3} value={p.highlights} onChange={set("highlights")} /></Field>
        <Field label="Itinerary (one day per line: Title | Description)"><Textarea rows={6} value={p.itinerary} onChange={set("itinerary")} placeholder={"Arrival in Bali | Flower-garland welcome...\nKintamani & Ubud | Full-day tour..."} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Inclusions (one per line)"><Textarea rows={4} value={p.inclusions} onChange={set("inclusions")} /></Field>
          <Field label="Exclusions (one per line)"><Textarea rows={4} value={p.exclusions} onChange={set("exclusions")} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hotels"><Textarea rows={2} value={p.hotels} onChange={set("hotels")} /></Field>
          <Field label="Transport"><Textarea rows={2} value={p.transport} onChange={set("transport")} /></Field>
        </div>
        <Field label="Important Notes (one per line)"><Textarea rows={3} value={p.notes} onChange={set("notes")} /></Field>
        <Field label="Cancellation"><Textarea rows={2} value={p.cancellation} onChange={set("cancellation")} /></Field>
        <Field label="FAQs (one per line: Question | Answer)"><Textarea rows={3} value={p.faqs} onChange={set("faqs")} /></Field>
      </div>
      <div className="mt-5 flex gap-3">
        <Button onClick={save} disabled={busy} data-testid="pe-save" className="rounded-full bg-navy text-white hover:bg-royal">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Package"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

function GroupTourEditor({ initial, onSaved, onCancel }) {
  const [t, setT] = useState(() => ({
    ...initial, departures: csv(initial.departures), highlights: lines(initial.highlights),
  }));
  const [busy, setBusy] = useState(false);
  const isNew = !initial._saved;
  const set = (k) => (e) => setT((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const payload = { slug: t.slug.trim(), name: t.name.trim(), duration: t.duration, places: t.places, departures: arrFromCsv(t.departures), type: t.type, highlights: arrFromLines(t.highlights), image: t.image.trim(), description: t.description, price_from: t.price_from === "" || t.price_from == null ? null : parseInt(t.price_from, 10), price_basis: t.price_basis || "per person · twin sharing", valid_till: t.valid_till || "" };
    try {
      if (isNew) await adminApi().post("/admin/group-tours", payload);
      else await adminApi().put(`/admin/group-tours/${initial.slug}`, payload);
      toast.success("Group tour saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="tour-editor">
      <h3 className="font-heading text-lg font-bold text-navy">{isNew ? "New Group Tour" : `Edit: ${initial.name}`}</h3>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Field label="Slug*"><Input value={t.slug} onChange={set("slug")} disabled={!isNew} /></Field>
        <Field label="Tour Name*"><Input value={t.name} onChange={set("name")} /></Field>
        <Field label="Duration"><Input value={t.duration} onChange={set("duration")} placeholder="5 Nights / 6 Days" /></Field>
        <Field label="Places"><Input value={t.places} onChange={set("places")} placeholder="Hanoi — Ha Long Bay" /></Field>
        <Field label="Departure Dates (comma)"><Input value={t.departures} onChange={set("departures")} placeholder="12 Sep 2026, 17 Oct 2026" /></Field>
        <Field label="Type">
          <select value={t.type} onChange={set("type")} className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
            <option>International Group Tour</option><option>India Group Tour</option>
          </select>
        </Field>
        <Field label="Image URL"><Input value={t.image} onChange={set("image")} /></Field>
        <Field label="Tentative Price ₹ / person"><Input type="number" value={t.price_from ?? ""} onChange={set("price_from")} placeholder="49500 (blank = on request)" data-testid="te-price" /></Field>
      </div>
      <div className="mt-4 grid gap-4">
        <Field label="Description"><Textarea rows={2} value={t.description} onChange={set("description")} /></Field>
        <Field label="Highlights (one per line)"><Textarea rows={3} value={t.highlights} onChange={set("highlights")} /></Field>
      </div>
      <div className="mt-5 flex gap-3">
        <Button onClick={save} disabled={busy} data-testid="te-save" className="rounded-full bg-navy text-white hover:bg-royal">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Tour"}</Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

function TestimonialEditor({ initial, onSaved, onCancel }) {
  const [t, setT] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;
  const set = (k) => (e) => setT((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const payload = { name: t.name, trip: t.trip, text: t.text, placeholder: !!t.placeholder };
    try {
      if (isNew) await adminApi().post("/admin/testimonials", payload);
      else await adminApi().put(`/admin/testimonials/${t.id}`, payload);
      toast.success("Testimonial saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="testimonial-editor">
      <h3 className="font-heading text-lg font-bold text-navy">{isNew ? "New Testimonial" : `Edit: ${initial.name}`}</h3>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Field label="Customer Name*"><Input value={t.name || ""} onChange={set("name")} /></Field>
        <Field label="Trip"><Input value={t.trip || ""} onChange={set("trip")} placeholder="Turkey" /></Field>
      </div>
      <div className="mt-4">
        <Field label="Review Text*"><Textarea rows={5} value={t.text || ""} onChange={set("text")} /></Field>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={!!t.placeholder} onChange={(e) => setT((s) => ({ ...s, placeholder: e.target.checked }))} />
        Mark as placeholder (shown with a "sample" badge)
      </label>
      <div className="mt-5 flex gap-3">
        <Button onClick={save} disabled={busy} data-testid="tm-save" className="rounded-full bg-navy text-white hover:bg-royal">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Testimonial"}</Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Enquiries");
  const [data, setData] = useState({ enquiries: [], bookings: [], packages: [], groupTours: [], testimonials: [], offers: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {kind, initial}
  const [offerSaving, setOfferSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = adminApi();
      const [enq, bkg, tst] = await Promise.all([
        api.get("/admin/enquiries"), api.get("/admin/bookings"), api.get("/admin/testimonials"),
      ]);
      const content = await adminApi().get("/content");
      setData({
        enquiries: enq.data.enquiries || [],
        bookings: bkg.data.bookings || [],
        testimonials: tst.data.testimonials || [],
        packages: content.data.packages || [],
        groupTours: content.data.group_tours || [],
        offers: content.data.offers || [],
      });
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/admin/login"); return; }
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);

  const del = async (path, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await adminApi().delete(path);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const paidCount = useMemo(() => data.bookings.filter((b) => b.payment_status === "paid").length, [data.bookings]);

  return (
    <div className="py-10 lg:py-14" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-header.png" alt="D Travel Link" className="h-12 w-auto" />
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-navy">Admin Dashboard</h1>
              <p className="text-xs text-slate-400">Manage enquiries, bookings, packages, tours & testimonials</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-royal hover:text-royal transition-colors">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={async () => { await adminLogout(); navigate("/admin/login"); }} data-testid="admin-logout"
                    className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-royal transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Enquiries", value: data.enquiries.length },
            { label: "Bookings (paid)", value: `${paidCount} / ${data.bookings.length}` },
            { label: "Packages", value: data.packages.length },
            { label: "Group Tours", value: data.groupTours.length },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
              <p className="text-xs font-bold tracking-wide uppercase text-slate-400">{s.label}</p>
              <p className="mt-1 font-heading text-2xl font-extrabold text-navy">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => { setTab(t); setEditing(null); }} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-royal hover:text-royal"}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-10 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</p>
        ) : (
          <div className="mt-6">
            {tab === "Enquiries" && (
              <div className="space-y-3" data-testid="admin-enquiries-list">
                {data.enquiries.length === 0 && <p className="text-slate-500">No enquiries yet.</p>}
                {data.enquiries.map((e) => (
                  <div key={e.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-heading font-bold text-navy">{e.name}</span>
                      <span className="rounded-full bg-sky-50 border border-sky-100 px-3 py-0.5 text-xs font-semibold text-royal">{e.trip_type}</span>
                      <span className="text-xs text-slate-400 ml-auto">{fmtDate(e.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      <a href={`tel:${e.phone}`} className="text-royal font-semibold">{e.phone}</a> · <a href={`mailto:${e.email}`} className="text-royal">{e.email}</a>
                      {e.destination && <> · {e.destination}</>}
                      {e.package_name && <> · <span className="font-semibold">{e.package_name}</span></>}
                      {e.travel_date && <> · {e.travel_date}</>}
                      {` · ${e.adults}A${e.children ? ` ${e.children}C` : ""}`}
                    </p>
                    {e.message && <p className="mt-2 text-sm text-slate-500 whitespace-pre-line">{e.message}</p>}
                  </div>
                ))}
              </div>
            )}

            {tab === "Bookings" && (
              <div className="space-y-3" data-testid="admin-bookings-list">
                {data.bookings.length === 0 && <p className="text-slate-500">No booking payments yet.</p>}
                {data.bookings.map((b) => (
                  <div key={b.session_id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-heading font-bold text-navy">{b.customer_name}</span>
                      <span className={`rounded-full px-3 py-0.5 text-xs font-bold uppercase tracking-wide ${b.payment_status === "paid" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                        {b.payment_status}
                      </span>
                      <span className="text-xs text-slate-400 ml-auto">{fmtDate(b.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      <span className="font-semibold">{b.package_name || "Custom booking"}</span> · ₹{Number(b.amount || 0).toLocaleString("en-IN")} {String(b.currency || "inr").toUpperCase()}
                      {" · "}<a href={`tel:${b.customer_phone}`} className="text-royal">{b.customer_phone}</a> · <a href={`mailto:${b.customer_email}`} className="text-royal">{b.customer_email}</a>
                      {b.travel_date && <> · {b.travel_date}</>}{b.travellers && <> · {b.travellers}</>}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {tab === "Packages" && (
              <div>
                {editing?.kind === "package" ? (
                  <PackageEditor initial={editing.initial} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <Button onClick={() => setEditing({ kind: "package", initial: EMPTY_PACKAGE })} data-testid="admin-new-package"
                            className="rounded-full bg-navy text-white hover:bg-royal"><Plus className="w-4 h-4 mr-1" /> New Package</Button>
                    <div className="mt-4 space-y-2" data-testid="admin-packages-list">
                      {data.packages.map((p) => (
                        <div key={p.slug} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-3.5 shadow-sm">
                          <img src={p.image} alt="" className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                          <div className="min-w-0">
                            <p className="font-heading font-bold text-navy truncate">{p.title}</p>
                            <p className="text-xs text-slate-400">{p.destination} · {p.nights}N/{p.days}D · {p.region}</p>
                          </div>
                          <div className="ml-auto flex gap-2">
                            <button onClick={() => setEditing({ kind: "package", initial: { ...p, _saved: true } })} data-testid={`admin-edit-package-${p.slug}`}
                                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-royal hover:border-royal transition-colors"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => del(`/admin/packages/${p.slug}`, p.title)} data-testid={`admin-delete-package-${p.slug}`}
                                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500 hover:border-rose-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "Group Tours" && (
              <div>
                {editing?.kind === "tour" ? (
                  <GroupTourEditor initial={editing.initial} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <Button onClick={() => setEditing({ kind: "tour", initial: { slug: "", name: "", duration: "", places: "", departures: [], type: "International Group Tour", highlights: [], image: "", description: "" } })}
                            data-testid="admin-new-tour" className="rounded-full bg-navy text-white hover:bg-royal"><Plus className="w-4 h-4 mr-1" /> New Group Tour</Button>
                    <div className="mt-4 space-y-2">
                      {data.groupTours.map((t) => (
                        <div key={t.slug} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-3.5 shadow-sm">
                          <img src={t.image} alt="" className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                          <div className="min-w-0">
                            <p className="font-heading font-bold text-navy truncate">{t.name}</p>
                            <p className="text-xs text-slate-400">{t.duration} · {(t.departures || []).join(", ")}</p>
                          </div>
                          <div className="ml-auto flex gap-2">
                            <button onClick={() => setEditing({ kind: "tour", initial: { ...t, _saved: true } })} data-testid={`admin-edit-tour-${t.slug}`}
                                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-royal hover:border-royal transition-colors"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => del(`/admin/group-tours/${t.slug}`, t.name)} data-testid={`admin-delete-tour-${t.slug}`}
                                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500 hover:border-rose-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "Testimonials" && (
              <div>
                {editing?.kind === "testimonial" ? (
                  <TestimonialEditor initial={editing.initial} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <Button onClick={() => setEditing({ kind: "testimonial", initial: { name: "", trip: "", text: "", placeholder: false } })}
                            data-testid="admin-new-testimonial" className="rounded-full bg-navy text-white hover:bg-royal"><Plus className="w-4 h-4 mr-1" /> New Testimonial</Button>
                    <div className="mt-4 space-y-2">
                      {data.testimonials.map((t) => (
                        <div key={t.id} className="rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow-sm">
                          <div className="flex items-center gap-2">
                            <p className="font-heading font-bold text-navy">{t.name} <span className="text-xs font-normal text-slate-400">· {t.trip}</span></p>
                            {t.placeholder && <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">placeholder</span>}
                            <div className="ml-auto flex gap-2">
                              <button onClick={() => setEditing({ kind: "testimonial", initial: t })} data-testid={`admin-edit-testimonial-${t.id}`}
                                      className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-royal hover:border-royal transition-colors"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => del(`/admin/testimonials/${t.id}`, t.name)} data-testid={`admin-delete-testimonial-${t.id}`}
                                      className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500 hover:border-rose-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          <p className="mt-2 text-sm text-slate-600 line-clamp-2">{t.text}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {tab === "Offers" && (
              <div>
                {editing?.kind === "offer" ? (
                  <OfferEditor
                    initial={editing.initial}
                    packages={data.packages}
                    saving={offerSaving}
                    onCancel={() => setEditing(null)}
                    onSave={async (payload) => {
                      setOfferSaving(true);
                      try {
                        if (editing.initial.id) await adminApi().put(`/admin/offers/${editing.initial.id}`, payload);
                        else await adminApi().post("/admin/offers", payload);
                        toast.success(editing.initial.id ? "Offer updated" : "Offer created");
                        setEditing(null);
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setOfferSaving(false); }
                    }}
                  />
                ) : (
                  <>
                    <Button onClick={() => setEditing({ kind: "offer", initial: {} })}
                            data-testid="admin-new-offer" className="rounded-full bg-navy text-white hover:bg-royal"><Plus className="w-4 h-4 mr-1" /> New Offer</Button>
                    <div className="mt-4 space-y-2" data-testid="admin-offers-list">
                      {data.offers.length === 0 && <p className="text-slate-500">No offers yet. Create one to show it on the homepage Travel Offers strip.</p>}
                      {data.offers.map((o) => (
                        <div key={o.id} className="rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow-sm" data-testid={`admin-offer-row-${o.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-heading font-bold text-navy">{o.title}</p>
                            {o.tag && <span className="rounded-full bg-red-50 border border-red-100 px-2.5 py-0.5 text-[10px] font-bold uppercase text-red-600">{o.tag}</span>}
                            {!o.active && <span className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">hidden</span>}
                            <span className="text-xs text-slate-400">valid till {o.valid_till}</span>
                            <div className="ml-auto flex gap-2">
                              <button onClick={() => setEditing({ kind: "offer", initial: o })} data-testid={`admin-edit-offer-${o.id}`}
                                      className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-royal hover:border-royal transition-colors"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => del(`/admin/offers/${o.id}`, o.title)} data-testid={`admin-delete-offer-${o.id}`}
                                      className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500 hover:border-rose-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          <p className="mt-1.5 text-sm text-slate-600">
                            {o.was_price ? <span className="line-through text-slate-400 mr-2">₹{Number(o.was_price).toLocaleString("en-IN")}</span> : null}
                            <span className="font-bold text-navy">₹{Number(o.now_price).toLocaleString("en-IN")}</span>
                            {o.package_slug ? <span className="text-xs text-slate-400"> · linked to {o.package_slug}</span> : null}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
